import axios from 'axios';
import keycloakAuth from './keycloakAuth';
import {
  completeWorkspaceCallback,
  leaveWorkspace,
  readWorkspaceCallback,
  readWorkspaceGuest,
  refreshWorkspaceGuest,
  renderWorkspaceFailure
} from '../../../shared/scale-workspace.mjs';

export const WORKSPACE_CHANGED_EVENT = 'scale-workspace-changed';

function actorSubject() {
  return keycloakAuth.keycloak?.tokenParsed?.sub || null;
}

function announce(guest) {
  window.dispatchEvent(new CustomEvent(WORKSPACE_CHANGED_EVENT, { detail: guest }));
  return guest;
}

/** Display state of the member's Scale workspace, or null when personal. */
export function currentWorkspaceGuest() {
  return readWorkspaceGuest(localStorage, actorSubject());
}

export function isWorkspaceGuest() {
  return Boolean(currentWorkspaceGuest());
}

/**
 * Runs on /scale-workspace/callback after Keycloak initializes. Returns true
 * when this page load was the callback (the dashboard must not mount).
 */
export async function handleWorkspaceCallback() {
  const callback = readWorkspaceCallback(window.location);
  if (!callback) return false;

  const result = await completeWorkspaceCallback({
    http: axios,
    storage: localStorage,
    callback,
    actorSubject: actorSubject(),
    isAuthenticated: () => keycloakAuth.isAuthenticated(),
    login: () => keycloakAuth.login({ redirectUri: window.location.href })
  });

  if (result.status === 'redirect') {
    // A stored GrooveSell resume target must not pull the guest elsewhere.
    localStorage.removeItem('redirect');
    window.location.replace(result.url);
  } else if (result.status === 'failed') {
    window.history.replaceState(null, '', '/scale-workspace/callback');
    renderWorkspaceFailure(document, result.reason);
  }
  return true;
}

/**
 * Confirms the workspace with gd-apis. Waits (briefly) only when this browser
 * already shows a workspace, so the member's own free-account lock cannot
 * redirect a guest before the state is known.
 */
export async function syncWorkspaceGuest() {
  if (!keycloakAuth.isAuthenticated()) return null;
  const refresh = refreshWorkspaceGuest({
    http: axios,
    storage: localStorage,
    actorSubject: actorSubject()
  }).then(announce);

  if (!currentWorkspaceGuest()) return null;
  const timeout = new Promise(resolve => setTimeout(() => resolve(currentWorkspaceGuest()), 3000));
  return Promise.race([refresh, timeout]);
}

/** Ends the guest session and reloads the dashboard in the member's account. */
export async function returnToOwnAccount() {
  try {
    await leaveWorkspace({ http: axios, storage: localStorage });
  } catch (error) {
    console.error('Leaving the Scale workspace failed:', error);
  }
  announce(null);
  window.location.replace('/');
}
